import apiClient from './api';
import { logError } from 'core/utils/logger';

// WebSocket message types
export enum WebSocketMessageType {
  // Connection messages
  CONNECTED = 'connected',
  ERROR = 'error',
  PING = 'ping',
  PONG = 'pong',
  
  // Project-specific messages
  ARTIFACT_CREATED = 'artifact_created',
  ARTIFACT_DELETED = 'artifact_deleted',
  ARTIFACT_FILE_UPLOADED = 'artifact_file_uploaded',
  ARTIFACT_PROCESSING_COMPLETED = 'artifact_processing_completed',
  FINDING_CREATED = 'finding_created',
  FINDING_DELETED = 'finding_deleted',
  FINDING_FILE_UPLOADED = 'finding_file_uploaded',
  
  // Report-specific messages
  REPORT_GENERATED = 'report_generated',
  
  // Chat-specific messages
  CHAT_STREAM = 'chat_stream',
  CHAT_MESSAGE_ADDED = 'chat_message_added'
}

// WebSocket message interface
export interface WebSocketMessage<T = any> {
  type: WebSocketMessageType;
  data: T;
}

// Connection status enum
export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

// WebSocket callbacks interface
export interface WebSocketCallbacks {
  onArtifactCreated?: (artifact: any) => void;
  onArtifactDeleted?: (data: { artifact_id: string; project_id: string }) => void;
  onArtifactFileUploaded?: (data: any) => void;
  onArtifactProcessingCompleted?: (data: any) => void;
  onFindingCreated?: (finding: any) => void;
  onFindingDeleted?: (data: { finding_id: string; project_id: string }) => void;
  onFindingFileUploaded?: (data: any) => void;
  onReportGenerated?: (report: any) => void;
  onChatStream?: (data: { chunk: string; is_final: boolean }) => void;
  onChatMessageAdded?: (message: any) => void;
  onConnectionStatusChange?: (status: ConnectionStatus) => void;
  onError?: (error: any) => void;
}

// Reconnection parameters
interface ReconnectionConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

/**
 * Class to manage WebSocket connections to the server
 */
export class WebSocketManager {
  // WebSocket instances
  private projectSocket: WebSocket | null = null;
  private chatSocket: WebSocket | null = null;
  
  // Connection state
  private projectId: string | null = null;
  private sessionId: string | null = null;
  private projectStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private chatStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  
  // Reconnection logic
  private projectRetryCount = 0;
  private chatRetryCount = 0;
  private projectReconnectTimer: NodeJS.Timeout | null = null;
  private chatReconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  
  // Callbacks for handling messages
  private callbacks: WebSocketCallbacks = {};
  
  // Reconnection configuration - more aggressive for scan environments
  private reconnectConfig: ReconnectionConfig = {
    maxRetries: 15,  // Increased from 10
    baseDelay: 500,  // Reduced from 1000 for faster reconnection
    maxDelay: 15000  // Reduced from 30000 for more frequent attempts
  };
  
  /**
   * Connect to the project WebSocket
   * @param projectId Project ID to connect to
   * @param callbacks Callbacks for handling messages
   */
  connectToProject(projectId: string, callbacks: WebSocketCallbacks): void {
    this.projectId = projectId;
    this.callbacks = callbacks;
    this.projectRetryCount = 0;
    
    // Close existing connection if any
    this.closeProjectSocket();
    
    // Set connection status to connecting
    this.updateProjectStatus(ConnectionStatus.CONNECTING);
    
    // Create new WebSocket connection
    const baseUrl = apiClient.getBaseUrl().replace(/^http/, 'ws');
    // Use the correct API path prefix (/pentest/ui)
    const url = `${baseUrl}/pentest/ui/ws/project/${projectId}`;
    
    try {
      console.log(`Connecting to project WebSocket at ${url}`);
      this.projectSocket = new WebSocket(url);
      
      // Configure WebSocket event handlers
      this.setupProjectSocketHandlers();
      
      // Start heartbeat after connection
      this.startHeartbeat();
    } catch (error) {
      logError('Failed to connect to project WebSocket', error);
      this.updateProjectStatus(ConnectionStatus.ERROR);
      this.scheduleProjectReconnect();
      
      // Notify about the failure via callback
      if (this.callbacks.onError) {
        this.callbacks.onError({
          detail: `Failed to connect to WebSocket: ${error}`,
          fallback: 'Using polling fallback'
        });
      }
    }
  }
  
  /**
   * Connect to the chat WebSocket
   * @param projectId Project ID to connect to
   * @param sessionId Chat session ID to connect to
   * @param callbacks Callbacks for handling messages
   */
  connectToChat(projectId: string, sessionId: string, callbacks: WebSocketCallbacks): void {
    // Validate input parameters
    if (!projectId || !sessionId) {
      console.error('Invalid parameters for chat WebSocket connection:', { projectId, sessionId });
      if (callbacks.onError) {
        callbacks.onError({
          detail: 'Invalid parameters for chat WebSocket connection',
          fallback: 'Using SSE fallback for chat'
        });
      }
      return;
    }
    
    this.projectId = projectId;
    this.sessionId = sessionId;
    this.callbacks = { ...this.callbacks, ...callbacks };
    this.chatRetryCount = 0;
    
    // Close existing connection if any
    this.closeChatSocket();
    
    // Set connection status to connecting
    this.updateChatStatus(ConnectionStatus.CONNECTING);
    
    // Create new WebSocket connection
    const baseUrl = apiClient.getBaseUrl().replace(/^http/, 'ws');
    // Use the correct API path prefix (/pentest/ui)
    const url = `${baseUrl}/pentest/ui/ws/chat/${projectId}/${sessionId}`;
    
    try {
      console.log(`Connecting to chat WebSocket at ${url}`);
      this.chatSocket = new WebSocket(url);
      
      // Configure WebSocket event handlers
      this.setupChatSocketHandlers();
      
      // Start heartbeat for chat connection as well
      this.startHeartbeat();
    } catch (error) {
      logError('Failed to connect to chat WebSocket', error);
      this.updateChatStatus(ConnectionStatus.ERROR);
      this.scheduleChatReconnect();
      
      // Notify about the failure via callback
      if (this.callbacks.onError) {
        this.callbacks.onError({
          detail: `Failed to connect to chat WebSocket: ${error}`,
          fallback: 'Using SSE fallback for chat'
        });
      }
    }
  }
  
  /**
   * Configure project WebSocket event handlers
   */
  private setupProjectSocketHandlers(): void {
    if (!this.projectSocket) return;
    
    this.projectSocket.onopen = () => {
      console.log('Project WebSocket connected');
      this.updateProjectStatus(ConnectionStatus.CONNECTED);
      this.projectRetryCount = 0;
    };
    
    this.projectSocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WebSocketMessage;
        this.handleProjectMessage(message);
      } catch (error) {
        logError('Error parsing WebSocket message', error);
      }
    };
    
    this.projectSocket.onclose = (event) => {
      console.log('Project WebSocket closed:', event.code, event.reason);
      this.updateProjectStatus(ConnectionStatus.DISCONNECTED);
      this.scheduleProjectReconnect();
    };
    
    this.projectSocket.onerror = (error) => {
      logError('Project WebSocket error', error);
      this.updateProjectStatus(ConnectionStatus.ERROR);
      // The socket will close automatically after an error
    };
  }
  
  /**
   * Configure chat WebSocket event handlers
   */
  private setupChatSocketHandlers(): void {
    if (!this.chatSocket) return;
    
    this.chatSocket.onopen = () => {
      console.log('Chat WebSocket connected');
      this.updateChatStatus(ConnectionStatus.CONNECTED);
      this.chatRetryCount = 0;
    };
    
    this.chatSocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WebSocketMessage;
        this.handleChatMessage(message);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error?.message);
      }
    };
    
    this.chatSocket.onclose = (event) => {
      console.log('Chat WebSocket closed:', event.code, event.reason);
      this.updateChatStatus(ConnectionStatus.DISCONNECTED);
      this.scheduleChatReconnect();
    };
    
    this.chatSocket.onerror = (error) => {
      logError('Chat WebSocket error', error);
      this.updateChatStatus(ConnectionStatus.ERROR);
      // The socket will close automatically after an error
    };
  }
  
  /**
   * Handle incoming project WebSocket messages
   * @param message WebSocket message
   */
  private handleProjectMessage(message: WebSocketMessage): void {
    console.log('Received project WebSocket message:', message.type, message.data);
    console.log('Message source: Project WebSocket');
    
    switch (message.type) {
      case WebSocketMessageType.CONNECTED:
        // Connection confirmation
        break;
        
      case WebSocketMessageType.PONG:
        // Heartbeat response
        break;
        
      case WebSocketMessageType.ERROR:
        logError('WebSocket error', message.data);
        if (this.callbacks.onError) {
          this.callbacks.onError(message.data);
        }
        break;
        
      case WebSocketMessageType.ARTIFACT_CREATED:
        console.log('WebSocket: Processing ARTIFACT_CREATED event with data:', message.data);
        console.log('WebSocket: Full message:', JSON.stringify(message, null, 2));
        if (this.callbacks.onArtifactCreated) {
          console.log('WebSocket: Calling onArtifactCreated callback');
          try {
            this.callbacks.onArtifactCreated(message.data);
          } catch (error) {
            logError('WebSocket: Error in onArtifactCreated callback', error);
          }
        } else {
          console.warn('WebSocket: No onArtifactCreated callback registered');
        }
        break;
        
      case WebSocketMessageType.ARTIFACT_DELETED:
        if (this.callbacks.onArtifactDeleted) {
          this.callbacks.onArtifactDeleted(message.data);
        }
        break;
        
      case WebSocketMessageType.ARTIFACT_FILE_UPLOADED:
        if (this.callbacks.onArtifactFileUploaded) {
          this.callbacks.onArtifactFileUploaded(message.data);
        }
        break;
        
      case WebSocketMessageType.ARTIFACT_PROCESSING_COMPLETED:
        if (this.callbacks.onArtifactProcessingCompleted) {
          this.callbacks.onArtifactProcessingCompleted(message.data);
        }
        break;
        
      case WebSocketMessageType.FINDING_CREATED:
        console.log('WebSocket: Processing FINDING_CREATED event with data:', message.data);
        console.log('WebSocket: Full message:', JSON.stringify(message, null, 2));
        if (this.callbacks.onFindingCreated) {
          console.log('WebSocket: Calling onFindingCreated callback');
          try {
            this.callbacks.onFindingCreated(message.data);
          } catch (error) {
            logError('WebSocket: Error in onFindingCreated callback', error);
          }
        } else {
          console.warn('WebSocket: No onFindingCreated callback registered');
        }
        break;
        
      case WebSocketMessageType.FINDING_DELETED:
        if (this.callbacks.onFindingDeleted) {
          this.callbacks.onFindingDeleted(message.data);
        }
        break;
        
      case WebSocketMessageType.FINDING_FILE_UPLOADED:
        if (this.callbacks.onFindingFileUploaded) {
          this.callbacks.onFindingFileUploaded(message.data);
        }
        break;
        
      case WebSocketMessageType.REPORT_GENERATED:
        console.log('WebSocket: Processing REPORT_GENERATED event with data:', message.data);
        if (this.callbacks.onReportGenerated) {
          console.log('WebSocket: Calling onReportGenerated callback');
          this.callbacks.onReportGenerated(message.data);
        } else {
          console.warn('WebSocket: No onReportGenerated callback registered');
        }
        break;
        
      default:
        console.log('Unhandled project message type:', message.type);
    }
  }
  
  /**
   * Handle incoming chat WebSocket messages
   * @param message WebSocket message
   */
  private handleChatMessage(message: WebSocketMessage): void {
    console.log('Received chat message:', message.type, message.data);
    console.log('Message source: Chat WebSocket');
    
    switch (message.type) {
      case WebSocketMessageType.CONNECTED:
        // Connection confirmation
        break;
        
      case WebSocketMessageType.PONG:
        // Heartbeat response
        break;
        
      case WebSocketMessageType.ERROR:
        logError('WebSocket error', message.data);
        if (this.callbacks.onError) {
          this.callbacks.onError(message.data);
        }
        break;
        
      case WebSocketMessageType.CHAT_STREAM:
        if (this.callbacks.onChatStream) {
          this.callbacks.onChatStream(message.data);
        }
        break;
        
      case WebSocketMessageType.CHAT_MESSAGE_ADDED:
        console.log('Received chat message added event:', message.data);
        if (this.callbacks.onChatMessageAdded) {
          this.callbacks.onChatMessageAdded(message.data);
        }
        break;
      
      // Handle artifact and finding events that come through the chat socket
      case WebSocketMessageType.ARTIFACT_CREATED:
      case WebSocketMessageType.ARTIFACT_DELETED:
      case WebSocketMessageType.ARTIFACT_FILE_UPLOADED:
      case WebSocketMessageType.ARTIFACT_PROCESSING_COMPLETED:
      case WebSocketMessageType.FINDING_CREATED:
      case WebSocketMessageType.FINDING_DELETED:
      case WebSocketMessageType.FINDING_FILE_UPLOADED:
      case WebSocketMessageType.REPORT_GENERATED:
        console.log('Forwarding message from chat socket to project message handler:', message.type, 'with data:', message.data);
        // Forward these messages to the project message handler
        this.handleProjectMessage(message);
        break;
        
      default:
        console.log('Unhandled chat message type:', message.type);
    }
  }
  
  /**
   * Update project WebSocket connection status
   * @param status New connection status
   */
  private updateProjectStatus(status: ConnectionStatus): void {
    this.projectStatus = status;
    if (this.callbacks.onConnectionStatusChange) {
      this.callbacks.onConnectionStatusChange(status);
    }
  }
  
  /**
   * Update chat WebSocket connection status
   * @param status New connection status
   */
  private updateChatStatus(status: ConnectionStatus): void {
    this.chatStatus = status;
    // Also notify with the callback to update UI if needed
    if (this.callbacks.onConnectionStatusChange) {
      // Pass the most critical status - if either connection has an error, show that
      const criticalStatus = this.getCriticalConnectionStatus();
      this.callbacks.onConnectionStatusChange(criticalStatus);
    }
  }
  
  /**
   * Get the most critical connection status between project and chat connections
   * Priority: ERROR > RECONNECTING > CONNECTING > DISCONNECTED > CONNECTED
   */
  private getCriticalConnectionStatus(): ConnectionStatus {
    // If either connection has an error, return ERROR
    if (this.projectStatus === ConnectionStatus.ERROR || this.chatStatus === ConnectionStatus.ERROR) {
      return ConnectionStatus.ERROR;
    }
    
    // If either connection is reconnecting, return RECONNECTING
    if (this.projectStatus === ConnectionStatus.RECONNECTING || this.chatStatus === ConnectionStatus.RECONNECTING) {
      return ConnectionStatus.RECONNECTING;
    }
    
    // If either connection is connecting, return CONNECTING
    if (this.projectStatus === ConnectionStatus.CONNECTING || this.chatStatus === ConnectionStatus.CONNECTING) {
      return ConnectionStatus.CONNECTING;
    }
    
    // If either connection is disconnected, return DISCONNECTED
    if (this.projectStatus === ConnectionStatus.DISCONNECTED || this.chatStatus === ConnectionStatus.DISCONNECTED) {
      return ConnectionStatus.DISCONNECTED;
    }
    
    // Otherwise, return CONNECTED
    return ConnectionStatus.CONNECTED;
  }
  
  /**
   * Schedule reconnection attempt for project WebSocket
   */
  private scheduleProjectReconnect(): void {
    if (this.projectReconnectTimer) {
      clearTimeout(this.projectReconnectTimer);
    }
    
    if (this.projectRetryCount >= this.reconnectConfig.maxRetries) {
      console.error('Max reconnection attempts reached for project WebSocket');
      this.updateProjectStatus(ConnectionStatus.ERROR);
      return;
    }
    
    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.reconnectConfig.baseDelay * Math.pow(1.5, this.projectRetryCount),
      this.reconnectConfig.maxDelay
    );
    
    this.updateProjectStatus(ConnectionStatus.RECONNECTING);
    console.log(`Reconnecting to project WebSocket in ${delay}ms (attempt ${this.projectRetryCount + 1})`);
    
    this.projectReconnectTimer = setTimeout(() => {
      if (this.projectId) {
        this.projectRetryCount++;
        this.connectToProject(this.projectId, this.callbacks);
      }
    }, delay);
  }
  
  /**
   * Schedule reconnection attempt for chat WebSocket
   */
  private scheduleChatReconnect(): void {
    if (this.chatReconnectTimer) {
      clearTimeout(this.chatReconnectTimer);
    }
    
    if (this.chatRetryCount >= this.reconnectConfig.maxRetries) {
      console.error('Max reconnection attempts reached for chat WebSocket');
      this.updateChatStatus(ConnectionStatus.ERROR);
      return;
    }
    
    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.reconnectConfig.baseDelay * Math.pow(1.5, this.chatRetryCount),
      this.reconnectConfig.maxDelay
    );
    
    this.updateChatStatus(ConnectionStatus.RECONNECTING);
    console.log(`Reconnecting to chat WebSocket in ${delay}ms (attempt ${this.chatRetryCount + 1})`);
    
    this.chatReconnectTimer = setTimeout(() => {
      if (this.projectId && this.sessionId) {
        this.chatRetryCount++;
        this.connectToChat(this.projectId, this.sessionId, this.callbacks);
      }
    }, delay);
  }
  
  /**
   * Start heartbeat to keep WebSocket connection alive
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    
    // More frequent heartbeat for scan environments to maintain connection
    this.heartbeatInterval = setInterval(() => {
      this.sendPing();
    }, 20000); // Reduced from 30 seconds to 20 seconds
    
    console.log('Heartbeat started for WebSocket connections (20s interval)');
  }
  
  /**
   * Send ping message to keep WebSocket connection alive
   */
  private sendPing(): void {
    if (this.projectSocket && this.projectSocket.readyState === WebSocket.OPEN) {
      this.projectSocket.send(JSON.stringify({ type: WebSocketMessageType.PING }));
    }
    
    if (this.chatSocket && this.chatSocket.readyState === WebSocket.OPEN) {
      this.chatSocket.send(JSON.stringify({ type: WebSocketMessageType.PING }));
    }
  }
  
  /**
   * Close project WebSocket connection
   */
  private closeProjectSocket(): void {
    if (this.projectSocket) {
      this.projectSocket.onclose = null; // Prevent reconnection on intentional close
      this.projectSocket.close();
      this.projectSocket = null;
    }
    
    if (this.projectReconnectTimer) {
      clearTimeout(this.projectReconnectTimer);
      this.projectReconnectTimer = null;
    }
  }
  
  /**
   * Close chat WebSocket connection
   */
  private closeChatSocket(): void {
    if (this.chatSocket) {
      this.chatSocket.onclose = null; // Prevent reconnection on intentional close
      this.chatSocket.close();
      this.chatSocket = null;
    }
    
    if (this.chatReconnectTimer) {
      clearTimeout(this.chatReconnectTimer);
      this.chatReconnectTimer = null;
    }
  }
  
  /**
   * Disconnect from all WebSocket connections
   */
  disconnect(): void {
    this.closeProjectSocket();
    this.closeChatSocket();
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    this.updateProjectStatus(ConnectionStatus.DISCONNECTED);
    this.updateChatStatus(ConnectionStatus.DISCONNECTED);
  }
  
  /**
   * Get project WebSocket connection status
   */
  getProjectStatus(): ConnectionStatus {
    return this.projectStatus;
  }
  
  /**
   * Get chat WebSocket connection status
   */
  getChatStatus(): ConnectionStatus {
    return this.chatStatus;
  }
  
  /**
   * Force reconnection to project WebSocket
   */
  reconnectToProject(): void {
    if (this.projectId) {
      this.projectRetryCount = 0;
      this.connectToProject(this.projectId, this.callbacks);
    }
  }
  
  /**
   * Force reconnection to chat WebSocket
   */
  reconnectToChat(): void {
    if (this.projectId && this.sessionId) {
      this.chatRetryCount = 0;
      this.connectToChat(this.projectId, this.sessionId, this.callbacks);
    }
  }
  
  /**
   * Set up a fallback polling mechanism for project updates
   * @param projectId Project ID to poll for
   * @param interval Polling interval in milliseconds (default: 5000ms)
   */
  setupPollingFallback(projectId: string, interval: number = 5000): NodeJS.Timeout {
    console.log(`Setting up polling fallback for project ${projectId} at ${interval}ms intervals`);
    
    // Import the services needed for polling using ES6 imports
    // We're importing from our own file context to avoid circular dependencies
    import('./artifactService').then(module => {
      const artifactService = module.artifactService;
      import('./index').then(indexModule => {
        const findingService = indexModule.findingService;
        
        // Set up the polling interval
        return setInterval(async () => {
          try {
            // Fetch artifacts, findings, and reports
            const artifacts = await artifactService.getArtifacts(projectId);
            const findings = await findingService.getFindings(projectId);
            
            // Import reportService dynamically to avoid circular dependencies
            import('./reportService').then(async (reportModule) => {
              const reportService = reportModule.reportService;
              const reports = await reportService.getReports(projectId);
            
              // Notify about new reports if any
              if (reports && reports.length > 0) {
                console.log(`Polling fallback: Found ${reports.length} reports`);
                
                // Notify callbacks if they exist
                reports.forEach(report => {
                  if (this.callbacks.onReportGenerated) {
                    this.callbacks.onReportGenerated(report);
                  }
                });
              }
            }).catch(error => {
              logError('Error importing reportService', error);
            });
            
            // Notify about new artifacts if any
            if (artifacts && artifacts.length > 0) {
              console.log(`Polling fallback: Found ${artifacts.length} artifacts`);
              
              // Notify callbacks if they exist
              artifacts.forEach(artifact => {
                if (this.callbacks.onArtifactCreated) {
                  this.callbacks.onArtifactCreated(artifact);
                }
              });
            }
            
            // Notify about new findings if any
            if (findings && findings.length > 0) {
              console.log(`Polling fallback: Found ${findings.length} findings`);
              
              // Notify callbacks if they exist
              findings.forEach(finding => {
                if (this.callbacks.onFindingCreated) {
                  this.callbacks.onFindingCreated(finding);
                }
              });
            }
          } catch (error) {
            logError('Error in polling fallback', error);
            
            // Notify about the error if callback exists
            if (this.callbacks.onError) {
              this.callbacks.onError({
                detail: `Polling fallback error: ${error}`,
                fallback: 'Attempting to continue polling'
              });
            }
          }
        }, interval);
      });
    });
    
    // Return a dummy interval for now - it will be replaced by the real one
    // once the imports are resolved
    return setInterval(() => {}, interval);
  }
}

// Export singleton instance
export const websocketService = new WebSocketManager();
export default websocketService;